import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { injectIntl, intlShape } from 'react-intl';
import { graphql, gql, compose } from 'react-apollo';

import LinearProgress from 'material-ui/LinearProgress';

import UploadPapers from '../UploadPapers';
import ParticipantsTable from '../ParticipantsTable';

import { parseFileName } from '../../utils/parseFileName';
import getGender from '../../utils/getGender';
import sortParticipants from '../../utils/sortParticipants';
import promiseMapSeries from '../../utils/promiseMapSeries';
import fileToBase64 from '../../utils/fileToBase64';

const createUploadedPaperMutation = gql`
    mutation CreateUploadedPaperMutation($townId: ID!, $file: String!, $filename: String!) {
        createUploadedPaper(townId: $townId, file: $file, filename: $filename) {
            _id
            filename
            file
            pages
        }
    }
`;

const registerTownParticipantsMutation = gql`
    mutation RegisterTownParticipantsMutation($townId: ID!, $participants: [ParticipantInput]) {
        registerTownParticipants(townId: $townId, participants: $participants) {
            _id
        }
    }
`;

class UploadTownPapers extends Component {
    static propTypes = {
        intl: intlShape.isRequired,
        createUploadedPaper: PropTypes.func.isRequired,
        registerTownParticipants: PropTypes.func.isRequired,
        onPapersUploaded: PropTypes.func,
        onParticipantsRegistered: PropTypes.func,
        town: PropTypes.object,
        season: PropTypes.string,
    };

    state = {
        step: 'upload',
    };

    handleUploadPapers = async files => {
        const {
            intl: { formatMessage },
            town: { name, _id: townId },
            onPapersUploaded,
            season,
        } = this.props;
        const townName = name.local || name.ru;

        let toLoad = files.length;
        this.setState({ loading: true, overall: toLoad, toLoad });

        const uploadedPapers = await promiseMapSeries(files, async file => {
            const content = await fileToBase64(file);

            const paper = await this.props.createUploadedPaper({
                variables: {
                    townId: townId,
                    file: content,
                    filename: file.name,
                },
            });

            this.setState({ toLoad: --toLoad });

            return paper;
        });

        let papers = uploadedPapers
            .map(({ data: { createUploadedPaper } }) => createUploadedPaper)
            .map(uploadedPaper => ({
                ...parseFileName(townName, uploadedPaper.filename, season),
                ...uploadedPaper,
            }));

        let participants = Object.create(null);

        papers.forEach((paper, index) => {
            if (!participants[paper.name]) {
                let gender = getGender(paper.name);

                if (gender) {
                    gender = formatMessage({ id: 'EnterData.gender.' + gender });
                }

                participants[paper.name] = {
                    name: paper.name,
                    grade: paper.grade,
                    email: '',
                    gender: gender,
                    school: '',
                    paperDrafts: [],
                    key: `${Math.random()}_${index}`,
                    errors: {
                        name: '',
                        grade: '',
                        gender: '',
                        school: '',
                        email: '',
                    },
                };
            }

            participants[paper.name].paperDrafts.push(paper);
            participants[paper.name].paperDrafts.sort(
                season === 'fall' ? (a, b) => (a.round === 'p' ? -1 : 1) : (a, b) => (a.round === 't' ? -1 : 1)
            );
        });

        this.setState({
            participants: sortParticipants(Object.keys(participants).map(key => participants[key])),
            genders: [formatMessage({ id: 'EnterData.gender.m' }), formatMessage({ id: 'EnterData.gender.f' })],
            step: 'enterData',
        });

        onPapersUploaded && onPapersUploaded();
    };

    handleSaveParticipants = participants => {
        const { town, registerTownParticipants, onParticipantsRegistered } = this.props;

        registerTownParticipants({
            variables: {
                townId: town._id,
                participants: participants.map(({ name, grade, school, email, gender, paperDrafts }) => {
                    grade = parseInt(grade, 10);
                    const { level: townLevel } = town.gradeSettings[grade] || town.tournament.gradeSettings[grade];

                    const papers = paperDrafts.map(({ round, file, filename, pages, level }) => ({
                        round,
                        grade,
                        file,
                        filename,
                        pages,
                        level: level || townLevel,
                    }));

                    return {
                        name,
                        gender: ['f', 'ж'].includes((gender || '').toLowerCase()) ? 'f' : 'm',
                        grade,
                        school,
                        email,
                        papers,
                    };
                }),
            },
        }).then(() => onParticipantsRegistered && onParticipantsRegistered());
    };

    render() {
        const {
            town: { name },
            season,
        } = this.props;
        const { step, loading, toLoad, overall } = this.state;

        return (
            <Fragment>
                {step === 'upload' &&
                    !loading && (
                        <UploadPapers
                            townName={name.local || name.ru}
                            season={season}
                            onSave={this.handleUploadPapers}
                        />
                    )}
                {step === 'upload' &&
                    loading && <LinearProgress mode="determinate" value={((overall - toLoad) / overall) * 100} />}
                {step === 'enterData' && (
                    <ParticipantsTable
                        editable
                        fullEdit
                        participants={this.state.participants}
                        onSave={this.handleSaveParticipants}
                        genders={this.state.genders}
                        season={season}
                    />
                )}
            </Fragment>
        );
    }
}

export default compose(
    graphql(createUploadedPaperMutation, { name: 'createUploadedPaper' }),
    graphql(registerTownParticipantsMutation, { name: 'registerTownParticipants' }),
    injectIntl
)(UploadTownPapers);



// WEBPACK FOOTER //
// ./src/Components/UploadTownPapers/UploadTownPapers.js